import React from "react";
import "./custom-form.css";
import Input from "components/input/Input";
import SearchableDropdown from "components/searchable-dropdown/SearchableDropdown";
import { DropdownOption } from "components/searchable-dropdown/types";

export interface CustomFormTextField {
  kind: "text" | "email" | "password";
  name: string;
  label: string;
  value: string;
  onChange: (val: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  autoComplete?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  readOnly?: boolean;
  disabled?: boolean;
  preserveSpaces?: boolean;
}

export interface CustomFormCheckboxField {
  kind: "checkbox";
  name: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Render the checkbox but block toggling (e.g. a value fixed by context). */
  disabled?: boolean;
}

export interface CustomFormSelectField<T extends string | number = number> {
  kind: "select";
  name: string;
  label: string;
  value: T | null;
  onChange: (val: T) => void;
  options: DropdownOption<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Render the dropdown but block interaction (e.g. a fixed/derived value). */
  disabled?: boolean;
}

export type CustomFormField =
  | CustomFormTextField
  | CustomFormCheckboxField
  | CustomFormSelectField<number>
  | CustomFormSelectField<string>;

interface Props {
  fields: CustomFormField[];
}

function renderField(field: CustomFormField) {
  switch (field.kind) {
    case "checkbox":
      return (
        <label
          key={field.name}
          className={`cf-checkbox-row${field.disabled ? " cf-checkbox-row--disabled" : ""}`}
          htmlFor={field.name}
        >
          <input
            id={field.name}
            type="checkbox"
            className="cf-checkbox-input"
            checked={field.checked}
            disabled={field.disabled}
            onChange={(e) => field.onChange(e.target.checked)}
          />
          <span className="cf-checkbox-box" aria-hidden="true" />
          <span className="cf-checkbox-label">{field.label}</span>
        </label>
      );

    case "select": {
      // The field is CustomFormSelectField<number> | <string>; widen to the
      // shared supertype so the (already-generic) dropdown renders either.
      const select = field as CustomFormSelectField<string | number>;
      return (
        <SearchableDropdown
          key={select.name}
          name={select.name}
          label={select.label}
          value={select.value}
          onChange={select.onChange}
          options={select.options}
          placeholder={select.placeholder}
          searchPlaceholder={select.searchPlaceholder}
          disabled={select.disabled}
        />
      );
    }

    default:
      return (
        <Input
          key={field.name}
          ref={field.inputRef}
          name={field.name}
          label={field.label}
          type={field.kind}
          value={field.value}
          onChange={field.onChange}
          onKeyDown={field.onKeyDown}
          placeholder={field.placeholder}
          autoComplete={field.autoComplete}
          readOnly={field.readOnly}
          disabled={field.disabled}
          preserveSpaces={field.preserveSpaces}
        />
      );
  }
}

function CustomForm({ fields }: Props) {
  return <div className="cf-form">{fields.map(renderField)}</div>;
}

export default CustomForm;
